import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { GlobalContext } from "./header/GlobalContext";
import StateBlockModal from "./modals/StateBlockModal.js";

// Utility to check cookie
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Location options (add accuracy/timing if needed)
const locationOptions = {};
const loggedIn = document.body.classList.contains('logged-in');

// Location success handler
const locationSuccess = (position, setShowModal, setGeolocation, setBlockedState) => {
  const latitude = position.coords.latitude;
  const longitude = position.coords.longitude;
  let form_data = new FormData();
  form_data.append('latitude', latitude);
  form_data.append('longitude', longitude);
  form_data.append('action', 'state_by_coords');

  axios.post('/wp-admin/admin-ajax.php', form_data)
    .then(response => {
      const usersState = response.data.state;

      if (!response.data.allowed) {
        setBlockedState(usersState);
      } else {
        setBlockedState(null);
      }

      setGeolocation(usersState);
      setShowModal(false);
    })
    .catch(error => {

      console.error('Error saving location:', error);
      setShowModal(true);
    });
};

// Location error handler
const locationError = (error, setShowModal) => {
  console.error('Error getting location: ', error.message);
  setShowModal(true);
};

// iOS native callback — called by Median once native geolocation is ready
window.median_geolocation_ready = () => {
  console.log("✅ median_geolocation_ready called!");
  if (navigator.geolocation && window.__setShowModal && window.__setGeolocation) {
    navigator.geolocation.getCurrentPosition(
      pos => locationSuccess(pos, window.__setShowModal, window.__setGeolocation),
      err => locationError(err, window.__setShowModal),
      locationOptions
    );
  } else {
    console.warn("median_geolocation_ready triggered, but handlers not set.");
  }
};

// Main location request logic
const requestLocation = (setShowModal, setGeolocation, setBlockedState) => {
  // Store state handlers globally for use in median callback
  window.__setShowModal = setShowModal;
  window.__setGeolocation = setGeolocation;

  const isLocalEnvironment = window.location.hostname === 'wagedwar.test';
  if (isLocalEnvironment) {
    console.log('Local environment detected – bypassing location modal.');
    setShowModal(false);
    setGeolocation({ latitude: 0, longitude: 0 }); // Mock geolocation for local testing
    return;
  }

  if (navigator.userAgent.includes('MedianIOS')) {
    console.log('Waiting for iOS native geolocation initialization...');
    // iOS will call median_geolocation_ready() when ready
    if (!navigator.geolocation) {
      console.error('iOS user does not have location services enabled.');
      setShowModal(true);
    }
  } else if (navigator.geolocation) {

    // Check for geolocation_state cookie
    // if (getCookie('geolocation_state')) {
    //   // Cookie exists, do not show modal or request location
    //   setShowModal(false);
    //   return;
    // }

    navigator.geolocation.getCurrentPosition(
      pos => locationSuccess(pos, setShowModal, setGeolocation, setBlockedState),
      err => locationError(err, setShowModal),
      locationOptions
    );
  } else {
    console.error('Geolocation is not supported by this browser.');
    setShowModal(true);
  }
};

const initializeLocation = (setShowModal, setGeolocation, setBlockedState) => {

  if (navigator.userAgent.includes('MedianIOS')) {
    // iOS: wait for median_geolocation_ready to trigger requestLocation
    window.__setShowModal = setShowModal;
    window.__setGeolocation = setGeolocation;
    console.log('iOS detected – waiting for native geolocation to be ready...');
  }

  requestLocation(setShowModal, setGeolocation, setBlockedState);
};

// Modal component
const Modal = () => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 9999,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }}>
    <div style={{
      backgroundColor: 'white',
      color: 'black',
      padding: '20px',
      borderRadius: '5px',
      textAlign: 'center',
      maxWidth: '95%',
    }}>
      <h2 style={{ fontSize: '40px' }} ><i className="fa-solid fa-location-dot"></i></h2>
      <p>Location access is required to verify if you are eligible to play.</p>
      <p>Please enable location sharing and reload page to continue.</p>

      <a href="#" style={{color: '#ce2423', textDecoration: 'underline'}} onClick={(e) => { e.preventDefault(); if (typeof setShowModal === 'function' && typeof setGeolocation === 'function' && typeof setBlockedState === 'function') { initializeLocation(setShowModal, setGeolocation, setBlockedState); } else { window.location.reload(); } }}>
        Click here to share your location again
      </a>

      <p>If you've denied location access, you can enable it in your browser settings.</p>

      <p>
        <a href="/how-to-enable-location-services/" target="_blank" rel="noopener noreferrer" style={{color: '#ce2423', textDecoration: 'underline'}}>
          How to enable location services
        </a>
      </p>

      {!navigator.userAgent.includes('median') && (
        <div style={{ marginTop: '24px', padding: '12px', background: '#f7f7f7', borderRadius: '6px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#222' }}>
            Alternatively, you can download the Native App
          </div>
          <a href="https://apps.apple.com/us/app/waged-war/id6458789465" target="_blank" rel="noopener noreferrer">
            <img
              width="120"
              height="40"
              src="https://assets.wagedwar.com/appstore.svg"
              alt="Waged War iOS App"
              className="img-fluid ls-is-cached lazyloaded"
              decoding="async"
              style={{ marginTop: '4px', marginRight: '7px' }}
            />
          </a>

          <a href="https://play.google.com/store/apps/details?id=com.WagedWar&hl=en_US" target="_blank" rel="noopener noreferrer">
            <img
              width="120"
              height="40"
              src="https://assets.wagedwar.com/wp-content/uploads/2025/06/18100709/googleplay-badge-1.webp"
              alt="Waged War iOS App"
              className="img-fluid ls-is-cached lazyloaded"
              decoding="async"
              style={{ marginTop: '4px' }}
            />
          </a>
        </div>
      )}

    </div>
  </div>
);

// Main component
const GeoLocate = () => {
  const { setGeolocation } = useContext(GlobalContext);
  const [showModal, setShowModal] = useState(false);
  const [blockedState, setBlockedState] = useState(null);

  useEffect(() => {
    initializeLocation(setShowModal, setGeolocation, setBlockedState);
  }, [setGeolocation]);

  // Helper to check for 'promo' param
  const urlParams = new URLSearchParams(window.location.search);
  const hasPromo = urlParams.has('promo');

  return (
    <>
      {/* {showModal && !hasPromo && <Modal />} */}
      {blockedState && <StateBlockModal usersState={blockedState} />}
    </>
  );
};

export default GeoLocate;
